// Slice for handling contacts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";
import { getUsageLimitMessage } from "../../../utils/usageLimitMessages";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Debouncing for API calls to prevent spam. fetchContacts is re-dispatched with the
// same default params after almost every mutation (add/delete/import/group changes) —
// unlike the other slices' background thunks, a long window here risks silently
// dropping a legitimate refresh after a second, distinct user action. Kept short:
// long enough to absorb same-tick duplicate mounts (the actual bug), short enough to
// never plausibly collide with two separate user-triggered mutations.
const debounceMap = new Map();
const DEBOUNCE_TIME = 600; // 0.6 seconds

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);

  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }

  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetContactsCaches = () => {
  debounceMap.clear();
};

// Async thunk for fetching contacts with pagination, search, filters, and sorting
export const fetchContacts = createAsyncThunk(
  "contacts/fetchContacts",
  async ({ page = 1, limit = 10, search = "", status = "all", labels = [], sortField = "name", sortDirection = "asc" } = {}, { rejectWithValue }) => {
    // Debounce check
    if (shouldDebounce(`fetchContacts_${page}_${limit}_${search}_${status}_${labels.join(",")}_${sortField}_${sortDirection}`)) {
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      // Add search parameter if provided
      if (search) {
        params.append('search', search);
      }

      // Add status filter if not "all"
      if (status && status !== "all") {
        params.append('status', status);
      }

      // Add label filters if provided
      if (labels && labels.length > 0) {
        params.append('labels', labels.join(','));
      }

      // Add sort parameters
      if (sortField) {
        params.append('sortField', sortField);
      }
      if (sortDirection) {
        params.append('sortDirection', sortDirection);
      }

      const response = await fetch(
        `${SERVER_DOMAIN}/api/contacts/get?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch contacts");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for adding a contact
export const addContact = createAsyncThunk(
  "contacts/addContact",
  async (contactData, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contacts/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(contactData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        const SUBSCRIPTION_CODES = ['PAYMENT_FAILED', 'SUBSCRIPTION_INACTIVE', 'PAYMENT_PENDING', 'TRIAL_EXPIRED'];
        if (SUBSCRIPTION_CODES.includes(errorData.code)) {
          return rejectWithValue(errorData.message || 'Your subscription is not active. Please update your plan.');
        }
        throw new Error(
          errorData.message || errorData.error || "Failed to add contact"
        );
      }

      const data = await response.json();
      return data.contact; // Return the newly added contact
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// Async thunk for bulk deleting contacts
export const bulkDeleteContacts = createAsyncThunk(
  "contacts/bulkDeleteContacts",
  async (ids, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contacts/bulkdelete`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ids }), // Send the IDs array in the request body
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete contacts");
      }
      return { deletedIds: ids }; // Return the IDs of the deleted contacts
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const bulkAddContacts = createAsyncThunk(
  "contacts/bulkAddContacts",
  async (arg, { rejectWithValue, dispatch }) => {
    // Either the contacts array (the Meta way, as always) or { contacts, target: { platform: "qr", connectionId } }
    // to import onto one QR number.
    const contactsData = Array.isArray(arg) ? arg : arg?.contacts || [];
    const qrTarget = !Array.isArray(arg) && arg?.target?.platform === "qr" ? arg.target : null;
    console.log("bulkAddContacts called with:", contactsData);

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    // Declare progressInterval outside try block for proper scope
    let progressInterval;
    
    try {
      // Initialize progress
      dispatch(setImportProgress({
        isImporting: true,
        current: 0,
        total: contactsData.length,
        message: "Starting import...",
      }));

      console.log("Sending request to API...");
      
      // Update progress to show processing
      dispatch(setImportProgress({
        current: 0,
        message: "Processing contacts...",
      }));

      // Simulate progress updates for better UX - only whole numbers
      progressInterval = setInterval(() => {
        dispatch((dispatch, getState) => {
          const currentState = getState();
          const currentProgress = currentState.contacts.importProgress.current;
          const total = currentState.contacts.importProgress.total;
          
          if (currentProgress < total * 0.8) {
            // Increment by whole numbers, max 5% of total per update
            const increment = Math.max(1, Math.floor(total * 0.02));
            const newProgress = Math.min(currentProgress + increment, Math.floor(total * 0.8));
            dispatch(setImportProgress({
              current: newProgress,
              message: "Importing contacts...",
            }));
          }
        });
      }, 300);

      const response = await fetch(
        `${SERVER_DOMAIN}/api/contacts/bulkadd${qrTarget ? `?platform=qr&connectionId=${encodeURIComponent(qrTarget.connectionId)}` : ""}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(contactsData),
        }
      );

      // Clear the progress interval
      clearInterval(progressInterval);

      if (!response.ok) {
        // Check if response is JSON or HTML
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          dispatch(setImportProgress({
            message: "Import failed!",
            isImporting: false,
          }));
          return rejectWithValue(
            errorData.message || errorData.error || "Failed to add contacts"
          );
        } else {
          // Handle HTML error response
          const errorText = await response.text();
          console.error("Server returned HTML instead of JSON:", errorText.substring(0, 200));
          dispatch(setImportProgress({
            message: "Import failed!",
            isImporting: false,
          }));
          return rejectWithValue("Server error - please try again later");
        }
      }

      const data = await response.json();
      console.log("Successful response:", data);

      // Update progress to completion
      dispatch(setImportProgress({
        current: contactsData.length,
        total: contactsData.length,
        message: "Import completed successfully!",
        isImporting: false,
      }));

      return data; // Return the full response object
    } catch (error) {
      // Clear progress interval if it exists
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      dispatch(setImportProgress({
        message: "Import failed!",
        isImporting: false,
      }));
      return rejectWithValue(error.message);
    }
  }
);


// Async thunk for updating a contact
export const updateContact = createAsyncThunk(
  "contacts/updateContact",
  async ({ id, updateData }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contacts/update/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update contact");
      }

      const data = await response.json();
      return data.contact; // Return the updated contact
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

//Async thunk for closing a contact
export const closeContact = createAsyncThunk(
  "contacts/closeContact",
  async ({ id, closeData }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();

    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contacts/closed-contacts/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(closeData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(getUsageLimitMessage(errorData));
      }

      const data = await response.json();
      return data.contact; // Return the closed contact
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const contactsSlice = createSlice({
  name: "contacts",
  initialState: {
    contacts: [],
    selectedContacts: [], // State for tracking selected contacts
    loading: false,
    error: null,
    searchTerm: "", // Add search term to initial state
    statusFilter: "all", // Add status filter to initial state
    sortField: "name", // Add sort field to initial state
    sortDirection: "asc", // Add sort direction to initial state
    importProgress: {
      isImporting: false,
      current: 0,
      total: 0,
      message: "",
    },
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalContacts: 0,
      hasNextPage: false,
      hasPrevPage: false,
      limit: 10
    }
  },
  reducers: {
    setSelectedContacts: (state, action) => {
      const contactId = action.payload;
      if (state.selectedContacts.includes(contactId)) {
        // If the contact is already selected, remove it
        state.selectedContacts = state.selectedContacts.filter(
          (id) => id !== contactId
        );
      } else {
        // Otherwise, add it to the selected contacts
        state.selectedContacts.push(contactId);
      }
    },

    setAllCurrentContacts: (state, action) => {
      const { ids, isRemove } = action.payload; // Expecting payload to contain 'ids' array and 'isRemove' boolean

      if (isRemove) {
        // Remove the IDs in 'ids' array from selectedContacts
        state.selectedContacts = state.selectedContacts.filter(
          (id) => !ids.includes(id)
        );
      } else {
        // Add the IDs in 'ids' array to selectedContacts (avoiding duplicates)
        ids.forEach((id) => {
          if (!state.selectedContacts.includes(id)) {
            state.selectedContacts.push(id);
          }
        });
      }
    },

    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },

    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },

    setSortField: (state, action) => {
      state.sortField = action.payload;
    },

    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },

    setImportProgress: (state, action) => {
      state.importProgress = { ...state.importProgress, ...action.payload };
    },

    resetImportProgress: (state) => {
      state.importProgress = {
        isImporting: false,
        current: 0,
        total: 0,
        message: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch contacts
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload.contacts;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.contacts = [];
          state.error = action.payload;
        }
      })
      // Update contact
      .addCase(updateContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.loading = false;
        const updatedContact = action.payload;
        const index = state.contacts.findIndex(
          (contact) => contact._id === updatedContact._id
        );
        if (index !== -1) {
          state.contacts[index] = updatedContact;
        }
        state.error = null;
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add contact
      .addCase(addContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addContact.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts.push(action.payload);
        state.pagination.totalContacts += 1;
        state.error = null;
      })
      .addCase(addContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk Add Contacts
      .addCase(bulkAddContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkAddContacts.fulfilled, (state, action) => {
        state.loading = false;
        // Don't try to merge contacts since payload is just a response object
        // The contacts will be refreshed by calling fetchContacts() after import
        state.error = null;
        // Keep the progress state as set by the thunk (shows completion)
        // The progress will be reset by the useEffect in the component
      })
      .addCase(bulkAddContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Keep import progress state for error display
      })
      // Bulk delete contacts
      .addCase(bulkDeleteContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteContacts.fulfilled, (state, action) => {
        state.loading = false;
        const { deletedIds } = action.payload;
        // Remove deleted contacts from the contacts array
        state.contacts = state.contacts.filter(
          (contact) => !deletedIds.includes(contact._id)
        );
        // Remove deleted contacts from the selectedContacts array
        state.selectedContacts = state.selectedContacts.filter(
          (id) => !deletedIds.includes(id)
        );
        // Update total count
        state.pagination.totalContacts = Math.max(0, state.pagination.totalContacts - deletedIds.length);
        state.error = null;
      })
      .addCase(bulkDeleteContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Close contact
      .addCase(closeContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(closeContact.fulfilled, (state, action) => {
        state.loading = false;
        const closedContact = action.payload;
        const index = state.contacts.findIndex(
          (contact) => contact._id === closedContact._id
        );
        if (index !== -1) {
          state.contacts[index] = closedContact;
        }
        state.error = null;
      })
      .addCase(closeContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setSelectedContacts, 
  setAllCurrentContacts, 
  setSearchTerm,
  setStatusFilter,
  setSortField,
  setSortDirection,
  setCurrentPage,
  setImportProgress,
  resetImportProgress
} = contactsSlice.actions;
export default contactsSlice.reducer;
